/**
 * @Author: v_jppjpeng
 * @Date: 2023-07-13 10:06:26
 * @Explain: 组合表单
 */

import { CheckboxGroup, RadioGroup, Select } from '@/components/tdesign';
import { intlGet } from '@/locales';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  Form,
  FormItemProps,
  FormProps,
  FormRules,
  FormValidateParams,
  FormValidateResult,
  Input,
  Textarea,
} from 'tdesign-react';
// type
import {
  InputEnum,
  StoreKeyEnum,
  TdPromptMappingTableType,
} from '@/definition/common/data.d';
import { InputSizeType } from '../data.d';
// utils
import { isEffectiveValue, isNumber } from '@/utils/common/dataJudgment';
import { getLocalStorage } from '@/utils/global';

export type PropsType = {
  key?: string;
  className?: string;
  /** 禁用 */
  disabled?: boolean;
  /** 隐藏label */
  hideLabel?: boolean;
  /** 超出隐藏label */
  ellipsisLabel?: boolean;
  /** 隐藏必填（*）
   *
   * 输入框有多个，均必填，无*
   * 输入框只有一个，必填，无*
   * 输入框有多个，个别必填，必填带*
   */
  hideRequired?: boolean;
  /** 表单样式风格
   * 纯文本 - plain-text
   */
  formStyle?: 'plain-text';
  /** 表单字段标签对齐方式：左对齐、右对齐、顶部对齐 */
  labelAlign?: FormProps['labelAlign'];
  /** label宽度，用来中英切换不同的宽度 */
  labelWidthArr?: number[];
  /** 表单大小 */
  inputSize?: InputSizeType;
  /** 表单格式 */
  promptMappingTable: TdPromptMappingTableType[];
  /** 详情数据 */
  details?: any;
  formProps?: FormProps;
  /** 获取表单纯净的校验结果 */
  getValidateFormOnly?: (status: boolean) => void;
};

export type RefsType = {
  onSubmit: () => {
    code: string;
    data?: any;
  };
};

export interface FormGroupRef {
  /** 设置表单值 */
  setFieldsValue: (params: any) => void;
  /** 获取表单数据 */
  getFieldsValue: () => any;
  /** 验证表单 */
  validateForm: (
    params?: FormValidateParams,
  ) => Promise<FormValidateResult<FormData>>;
  /** 重置表单 */
  resetForm: () => void;
  /** 清除校验结果 */
  clearValidate: () => void;
}

const FormGroup = (props: PropsType, refs: any) => {
  const {
    key,
    className,
    disabled,
    hideLabel,
    ellipsisLabel,
    hideRequired,
    formStyle,
    formProps,
    labelAlign,
    labelWidthArr,
    inputSize,
    promptMappingTable,
    details,
    getValidateFormOnly,
  } = props;

  const [form] = Form.useForm();

  /** 设置表单值 */
  const setFieldsValue = (params: any) => form.setFieldsValue(params);
  /** 获取表单数据 */
  const getFieldsValue = () => form.getFieldsValue(true);
  /** 验证表单 */
  const validateForm = (params?: FormValidateParams) => form.validate(params);
  /** 重置表单 */
  const resetForm = () => form.reset();
  /** 清除校验结果 */
  const clearValidate = () => form.clearValidate();

  // label width - 临时处理方案
  const labelWidth = useMemo(() => {
    const [zhWidth, enWidth] = labelWidthArr || [];
    return getLocalStorage(StoreKeyEnum.DESIGN_LANGUAGE) === 'zh'
      ? zhWidth
      : enWidth;
  }, [labelWidthArr]);

  /** 提交表单 */
  // const onSubmit = async () => {
  //   let res: {
  //     code: string;
  //     data?: any;
  //   } = {
  //     code: '',
  //     data: null,
  //   };
  //   await form.validate().then((validateRes) => {
  //     if (validateRes === true) {
  //       const formData = form.getFieldsValue(true);
  //       res = {
  //         code: 'success',
  //         data: formData,
  //       };
  //     }
  //     res = {
  //       code: 'error',
  //     };
  //   });
  //   console.log('🚀 ~ file: index.tsx:75 ~ form.validate ~ res:', res);
  //   return res;
  // };

  const onRender = (item: TdPromptMappingTableType) => {
    const commonProps: any = {
      ...item.contentProps,
      size: inputSize,
      placeholder: item.placeholder || intlGet(item.placeholderKey),
    };
    const value = details?.[item.field];
    const options = (item.options || []).map((item) =>
      item.labelKey ? { ...item, label: intlGet(item.labelKey) } : { ...item },
    );
    const inputProps = {
      ...commonProps,
      placeholder: commonProps.placeholder || intlGet('COMMON.PLEASE.ENTER'),
    };
    const textareaProps = {
      ...commonProps,
      placeholder: commonProps.placeholder || intlGet('COMMON.PLEASE.ENTER'),
    };
    const selectProps = {
      ...commonProps,
      options,
      placeholder: commonProps.placeholder || intlGet('COMMON.PLEASE.SELECT'),
    };
    const radioGroupProps = {
      ...commonProps,
      options,
    };
    const checkboxGroupProps = {
      ...commonProps,
      options,
    };
    switch (item.inputType) {
      case InputEnum.hiddenInput:
        return <Input type="hidden" {...inputProps} />;
      case InputEnum.input:
        return item?.onAdornment ? (
          item.onAdornment(<Input {...inputProps} />)
        ) : (
          <Input {...inputProps} />
        );
      case InputEnum.textarea:
        return <Textarea rows={3} {...textareaProps} />;
      case InputEnum.select:
        return <Select {...selectProps} />;
      case InputEnum.radioGroup:
        return (
          <RadioGroup
            className="mt-5 mb-10"
            {...radioGroupProps}
            layout="column"
          />
        );
      case InputEnum.checkboxGroup:
        return (
          <CheckboxGroup
            className="mt-5 mb-10"
            {...checkboxGroupProps}
            layout="column"
          />
        );
      case InputEnum.text:
        const textRender = item?.render
          ? item?.render?.(value, details)
          : value;
        const handleText = isNumber(textRender)
          ? textRender || '0'
          : textRender || '-';
        return <span className="t-form__text">{handleText}</span>;
      case InputEnum.custom:
        return item?.render?.(form, details);
    }
  };
  const formRules: FormRules<any> = useMemo(() => {
    const filter: any = {};
    promptMappingTable.forEach((item) => {
      filter[item.field] = (item?.formRules || []).map((item) => ({
        ...item,
        message: item.messageKey ? intlGet(item.messageKey) : item.message,
      }));
    });
    return filter;
  }, [promptMappingTable, inputSize]);

  useEffect(() => {
    setTimeout(async () => {
      const status = await form.validateOnly();
      // 获取表单纯净的校验结果
      getValidateFormOnly?.(status === true ? true : false);
    }, 200);
  }, []);

  // 绑定ref，抛出方法
  useImperativeHandle(refs, () => ({
    setFieldsValue,
    getFieldsValue,
    validateForm,
    resetForm,
    clearValidate,
  }));

  return (
    <Form
      key={key}
      className={classNames(
        {
          'default-td-form': true,
          'hide-required': hideRequired,
          'hide-label': hideLabel,
          'ellipsis-label': ellipsisLabel,
          [`${formStyle}-form`]: formStyle,
          [`size-${inputSize}`]: inputSize,
        },
        className,
      )}
      resetType="initial"
      form={form}
      rules={formRules}
      labelWidth={labelWidth}
      labelAlign={labelAlign}
      disabled={disabled}
      onValuesChange={async () => {
        const status = await form.validateOnly();
        // 获取表单纯净的校验结果
        getValidateFormOnly?.(status === true ? true : false);
      }}
      {...formProps}
    >
      {promptMappingTable.map((item) => {
        const labelText = item.labelKey ? intlGet(item.labelKey) : item.label;
        const label = ellipsisLabel ? (
          <span title={labelText}>{labelText}</span>
        ) : (
          labelText
        );
        const help = item.helpKey ? intlGet(item.helpKey) : item.help;
        const itemProps: FormItemProps = {
          label,
          name: item.field,
          help,
          initialData: isEffectiveValue(item.initialData)
            ? item.initialData
            : '',
          ...item.formItemProps,
        };

        return item.formItemRender ? (
          item.formItemRender()
        ) : (
          <Form.FormItem
            key={item.field}
            className={classNames({
              hide: item.inputType === InputEnum.hiddenInput,
            })}
            {...itemProps}
          >
            {onRender(item)}
          </Form.FormItem>
        );
      })}
    </Form>
  );
};

export default forwardRef(FormGroup);
